/** Instance-owned simulation extracted from the separately maintained Steam Punk game.
 * The host supplies clock, randomness, DOM and lifecycle; no globals or score API are shared. */
export function installSteamSimulation(runtime) {
  runtime.update = function (dt) {
    if (runtime.game.status === 'paused' || runtime.game.status === 'gameover') return;
    runtime.game.gravityCalls = 0;
    runtime.game.gravityTrapCalls = 0;
    runtime.updateRuntimeQuality();
    runtime.tickChain(dt);
    runtime.updateModeTimers(dt);
    if (runtime.game.status === 'gameover') return;
    runtime.tickIdleCache(dt);
    if (runtime.game.launchQueue > 0) {
      runtime.game.launchDelay -= dt;
      if (runtime.game.launchDelay <= 0) {
        const spread = (runtime.game.ballCount - runtime.game.launchQueue) % 5;
        runtime.spawnBall(runtime.game.angle + (spread - 2) * 1.25, 0, {
          source: runtime.game.launchSource,
        });
        runtime.game.launchQueue -= 1;
        runtime.game.launchDelay = runtime.getLaunchQueueIntervalSeconds();
      }
    }
    runtime.updateTransientEffects(dt);
    runtime.game.screenFlash = Math.max(0, runtime.game.screenFlash - dt * 1.6);
    runtime.game.shake = Math.max(0, runtime.game.shake - dt * 2.8);
    runtime.game.launchFlash = Math.max(0, runtime.game.launchFlash - dt);
    runtime.applySpeedBottomPenalty();
    if (runtime.game.spatialDirty) runtime.rebuildBrickBuckets();
    for (const ball of runtime.game.balls) {
      ball.age += dt;
      runtime.tickPlanetCooldowns(ball, dt);
      runtime.applyGravity(ball, dt);
      runtime.rampBallSpeed(ball, dt);
      ball.glow = Math.max(0, ball.glow - dt * 1.8);
      runtime.pushTrailPoint(ball);
      ball.x += ball.vx * dt;
      ball.y += ball.vy * dt;
      if (ball.x - ball.r <= 0 || ball.x + ball.r >= runtime.W) {
        ball.x = runtime.clamp(ball.x, ball.r, runtime.W - ball.r);
        ball.vx *= -1;
        ball.sideBounceStreak += 1;
        ball.wallBounceStreak += 1;
        runtime.preventBounceStall(ball, 'side');
        runtime.spark(ball.x, ball.y, '#ff9f3d', 4);
        runtime.spendTemporaryHit(ball);
        if (ball.dead) continue;
      }
      if (ball.y - ball.r <= 0) {
        ball.y = ball.r;
        ball.vy *= -1;
        ball.sideBounceStreak = 0;
        ball.wallBounceStreak += 1;
        runtime.preventBounceStall(ball, 'top');
        runtime.spark(ball.x, ball.y, '#ffd166', 4);
        runtime.spendTemporaryHit(ball);
        if (ball.dead) continue;
      }
      runtime.collideBricks(ball);
      if (ball.dead) continue;
      runtime.collideOrbs(ball);
      runtime.collideWalls(ball);
      if (ball.dead) continue;
      runtime.collidePlanets(ball);
    }
    runtime.collideBalls();
    runtime.compactBoardBodies();
    runtime.processSpeedBlockAdvances(dt);
    runtime.compactBalls();
    runtime.syncNeutronDeploymentState();
    if (
      !runtime.isRapidLaunchMode() &&
      runtime.game.volleyActive &&
      runtime.game.launchQueue === 0 &&
      runtime.game.balls.length === 0
    ) {
      runtime.endVolley();
    }
  };
  runtime.compactBalls = function () {
    let write = 0;
    let activeUserBalls = 0;
    let activeNeutronBombs = 0;
    for (let read = 0; read < runtime.game.balls.length; read += 1) {
      const ball = runtime.game.balls[read];
      if (ball.dead || ball.y - ball.r > runtime.H + 80) continue;
      if (ball.source === 'user') activeUserBalls += 1;
      if (ball.neutron && ball.neutronDeploymentId === runtime.game.neutronDeploymentId)
        activeNeutronBombs += 1;
      runtime.game.balls[write] = ball;
      write += 1;
    }
    runtime.game.balls.length = write;
    runtime.game.activeUserBalls = activeUserBalls;
    runtime.game.activeNeutronBombs = activeNeutronBombs;
  };
  runtime.compactBoardBodies = function () {
    const previousWalls = runtime.game.walls.length;
    const previousPlanets = runtime.game.planets.length;
    let write = 0;
    for (let read = 0; read < runtime.game.walls.length; read += 1) {
      const wall = runtime.game.walls[read];
      if (wall.hp <= 0 || wall.y >= runtime.H) continue;
      runtime.game.walls[write] = wall;
      write += 1;
    }
    runtime.game.walls.length = write;
    write = 0;
    for (let read = 0; read < runtime.game.planets.length; read += 1) {
      const planet = runtime.game.planets[read];
      if (planet.y - planet.r >= runtime.H) continue;
      runtime.game.planets[write] = planet;
      write += 1;
    }
    runtime.game.planets.length = write;
    if (
      runtime.game.walls.length !== previousWalls ||
      runtime.game.planets.length !== previousPlanets
    )
      runtime.markBoardSceneDirty();
  };
  runtime.pushTrailPoint = function (ball) {
    const maxTrail =
      runtime.game.energyMode === 'pressureGuard'
        ? runtime.MOBILE_QUALITY
          ? 1
          : 2
        : runtime.MOBILE_QUALITY
          ? runtime.game.balls.length > 60
            ? 2
            : runtime.game.balls.length > 28
              ? 3
              : 5
          : runtime.game.balls.length > 90
            ? 3
            : runtime.game.balls.length > 45
              ? 5
              : 8;
    const trail = ball.trail;
    for (let i = Math.min(trail.length, maxTrail - 1); i > 0; i -= 1) {
      trail[i] = trail[i - 1];
    }
    if (!trail[0]) trail[0] = { x: ball.x, y: ball.y };
    else {
      trail[0].x = ball.x;
      trail[0].y = ball.y;
    }
    trail.length = Math.min(maxTrail, trail.length + 1);
  };
  runtime.updateTransientEffects = function (dt) {
    runtime.processQueuedEffects();
    let write = 0;
    for (let read = 0; read < runtime.game.particles.length; read += 1) {
      const particle = runtime.game.particles[read];
      particle.life -= dt;
      if (particle.life <= 0) continue;
      if (particle.type === 'coin' || particle.type === 'moneyBag') {
        particle.vy += 210 * dt;
        particle.spin += (particle.spinSpeed || 0) * dt;
      }
      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;
      runtime.game.particles[write] = particle;
      write += 1;
    }
    runtime.game.particles.length = write;
    write = 0;
    for (let read = 0; read < runtime.game.bursts.length; read += 1) {
      const burst = runtime.game.bursts[read];
      burst.life -= dt;
      if (burst.life <= 0) continue;
      burst.radius += burst.speed * dt;
      runtime.game.bursts[write] = burst;
      write += 1;
    }
    runtime.game.bursts.length = write;
    write = 0;
    for (let read = 0; read < runtime.game.floaters.length; read += 1) {
      const floater = runtime.game.floaters[read];
      floater.life -= dt;
      if (floater.life <= 0) continue;
      floater.y += floater.vy * dt;
      floater.x += floater.vx * dt;
      runtime.game.floaters[write] = floater;
      write += 1;
    }
    runtime.game.floaters.length = write;
    runtime.game.boardShiftCue = Math.max(0, runtime.game.boardShiftCue - dt);
  };
  runtime.rampBallSpeed = function (ball, dt) {
    const current = Math.hypot(ball.vx, ball.vy) || ball.baseSpeed;
    const ramp = 1 + Math.min(1.05, ball.age * 0.045);
    const target = Math.min(ball.maxSpeed, ball.baseSpeed * ramp);
    if (current >= target) return;
    const next = current + (target - current) * Math.min(1, dt * 2.2);
    ball.vx = (ball.vx / current) * next;
    ball.vy = (ball.vy / current) * next;
  };
  runtime.tickPlanetCooldowns = function (ball, dt) {
    for (const key in ball.planetCooldowns) {
      ball.planetCooldowns[key] -= dt;
      if (ball.planetCooldowns[key] <= 0) delete ball.planetCooldowns[key];
    }
  };
  runtime.applyGravity = function (ball, dt) {
    let activeApplied = false;
    if (ball.activeGravityPlanetId !== null) {
      let active = null;
      for (let i = 0; i < runtime.game.planets.length; i += 1) {
        if (runtime.game.planets[i].id !== ball.activeGravityPlanetId) continue;
        active = runtime.game.planets[i];
        break;
      }
      if (active) activeApplied = runtime.applyPlanetGravity(ball, active, dt, true);
      else ball.activeGravityPlanetId = null;
    }
    for (const planet of runtime.game.planets) {
      if (activeApplied && planet.id === ball.activeGravityPlanetId) continue;
      runtime.applyPlanetGravity(ball, planet, dt, false);
    }
  };
  runtime.applyPlanetGravity = function (ball, planet, dt, allowReuse) {
    const dx = planet.x - ball.x;
    const dy = planet.y - ball.y;
    const rawDistSq = dx * dx + dy * dy;
    if (rawDistSq > planet.influenceSq) {
      if (ball.activeGravityPlanetId === planet.id) ball.activeGravityPlanetId = null;
      return false;
    }
    if (allowReuse && ball.gravityReuseFrames > 0) {
      ball.vx += ball.gravityFx * dt;
      ball.vy += ball.gravityFy * dt;
      ball.glow = Math.max(ball.glow, ball.gravityGlow);
      ball.gravityReuseFrames -= 1;
      runtime.game.gravityCalls += 1;
      runtime.game.gravityTrapCalls += 1;
      runtime.clampBallSpeed(ball, 3000);
      return true;
    }
    const distSq = Math.max(planet.coreSq, rawDistSq);
    const bucket = runtime.clamp(
      Math.floor(distSq * planet.invInfluenceSq * runtime.GRAVITY_LUT_SIZE),
      0,
      runtime.GRAVITY_LUT_SIZE - 1,
    );
    const row = planet.gravityLut[bucket];
    if (!row || row.force <= 0) return false;
    const invDist = row.invDist;
    const ax = dx * invDist * row.force;
    const ay = dy * invDist * row.force;
    const orbitDirection = ball.vx * dy - ball.vy * dx > 0 ? 1 : -1;
    const tx = -dy * invDist * row.tangent * orbitDirection;
    const ty = dx * invDist * row.tangent * orbitDirection;
    const fx = ax + tx;
    const fy = ay + ty;
    ball.vx += fx * dt;
    ball.vy += fy * dt;
    ball.glow = Math.max(ball.glow, row.glow);
    ball.activeGravityPlanetId = planet.id;
    runtime.game.gravityCalls += 1;
    if (row.proximity >= runtime.GRAVITY_REUSE_PROXIMITY) {
      ball.gravityFx = fx;
      ball.gravityFy = fy;
      ball.gravityGlow = row.glow;
      ball.gravityReuseFrames = 1;
      runtime.game.gravityTrapCalls += 1;
    }
    runtime.clampBallSpeed(ball, 3000);
    return true;
  };
  runtime.clampBallSpeed = function (ball, maxSpeed) {
    const speedSq = ball.vx * ball.vx + ball.vy * ball.vy;
    const maxSpeedSq = maxSpeed * maxSpeed;
    if (speedSq <= maxSpeedSq) return;
    const speed = Math.sqrt(speedSq);
    ball.vx = (ball.vx / speed) * maxSpeed;
    ball.vy = (ball.vy / speed) * maxSpeed;
  };
  runtime.collideBalls = function () {
    const buckets = runtime.scratchBallBuckets;
    buckets.clear();
    runtime.scratchCheckedPairs.clear();
    for (const ball of runtime.game.balls) {
      if (ball.dead) continue;
      const key = runtime.spatialKey(ball.x, ball.y);
      let bucket = buckets.get(key);
      if (!bucket) {
        bucket = [];
        buckets.set(key, bucket);
      }
      bucket.push(ball);
    }
    for (const a of runtime.game.balls) {
      if (a.dead) continue;
      const nearby = runtime.queryPointBuckets(buckets, a.x, a.y);
      for (let i = 0; i < nearby.length; i += 1) {
        const b = nearby[i];
        if (a === b || b.dead) continue;
        const minId = a.id < b.id ? a.id : b.id;
        const maxId = a.id < b.id ? b.id : a.id;
        const pairKey = minId * 1000000 + maxId;
        if (runtime.scratchCheckedPairs.has(pairKey)) continue;
        runtime.scratchCheckedPairs.add(pairKey);
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const minDist = a.r + b.r;
        const distSq = dx * dx + dy * dy;
        if (distSq <= 0 || distSq > minDist * minDist) continue;
        const dist = Math.sqrt(distSq);
        const nx = dx / dist;
        const ny = dy / dist;
        const overlap = (minDist - dist) / 2;
        a.x -= nx * overlap;
        a.y -= ny * overlap;
        b.x += nx * overlap;
        b.y += ny * overlap;
        const rvx = b.vx - a.vx;
        const rvy = b.vy - a.vy;
        const velocityAlongNormal = rvx * nx + rvy * ny;
        if (velocityAlongNormal > 0) continue;
        const impulse = -velocityAlongNormal;
        a.vx -= impulse * nx;
        a.vy -= impulse * ny;
        b.vx += impulse * nx;
        b.vy += impulse * ny;
        a.glow = Math.max(a.glow, 0.75);
        b.glow = Math.max(b.glow, 0.75);
        runtime.spark((a.x + b.x) / 2, (a.y + b.y) / 2, '#ffd166', 8);
        runtime.spendTemporaryHit(a, (a.x + b.x) / 2, (a.y + b.y) / 2);
        runtime.spendTemporaryHit(b, (a.x + b.x) / 2, (a.y + b.y) / 2);
      }
    }
  };
}
